import ApiCard from "@/api/ApiCard";
import ApiEnter from "@/api/ApiEnter";
import ApiHome from "@/api/ApiHome";
import ApiLinkSupport from "@/api/ApiLinkSupport";
import UtilError from "@/util/ValidateError/UtilError";
import { ELoginStep } from "@/struct/ELogin/ELoginStep";
import { IAppVersion } from "@/struct/IAppVersion/IAppVersion";
import { IDocument } from "@/struct/IDocument/IDocument";
import { ICard, IComission } from "@/struct/IHomePage/ICabinetBalance";
import { ILinkSupport } from "@/struct/ILinkSupport/ILinkSupport";
import {
  IAppInfo,
  IBrandStatus,
  IStoreState,
  IUserData,
} from "@/struct/IStore/IStore";
import { Capacitor } from "@capacitor/core";
import { createStore } from "vuex";
// import testPdf from "@/views/Documents/test-pdf.pdf";

export default createStore({
  state: {
    currentCommission: null,
    appName: null,
    appInfo: null,
    yandexAppEnabled: false,
    yandexClientId: null,
    isActive: null,
    platform: Capacitor.getPlatform(),
    isOpenNewVersionModal: false,
    versionReminderDismissed: false,
    lastReminderTime: null,
    currentAppVersion: "1.6.8",
    appVersion: null,
    connectionState: true,
    userData: null,
    brandStatus: null,
    linkSupport: null,
    userCards: undefined,
    deferredPrompt: null,
    currentLoginStep: ELoginStep.EnterPhone,
    isNeedRegistration: false,
    loginPhone: "",
    loginPassword: "",
    documents: [
      // {
      //   id: "1",
      //   name: "test-pdf.pdf",
      //   url: testPdf,
      //   size: "71 КБ",
      //   isNew: true,
      //   isSigned: false,
      // },
    ],
  },
  getters: {
    getBrandStatus(state: IStoreState): IBrandStatus | null {
      return state.brandStatus;
    },
    getCurrentCommission(state: IStoreState): IComission | null {
      return state.currentCommission;
    },
    shouldShowUpdateButton(state: IStoreState): boolean {
      return (
        state.appVersion?.detail !== "VERSION_SUCCESSES" &&
        (state.platform === "ios" || state.platform === "android")
      );
    },
    showNewVersionModal(state: IStoreState): boolean {
      return (
        state.isOpenNewVersionModal &&
        state.appVersion?.detail !== "VERSION_SUCCESSES" &&
        (state.platform === "ios" || state.platform === "android") &&
        (!state.versionReminderDismissed ||
          (state.lastReminderTime !== null &&
            Date.now() - state.lastReminderTime > 2 * 60 * 60 * 1000))
      ); // модалка обновления появляется каждые 2 часа
    },
    appName(state: IStoreState) {
      return state.appName;
    },
    yandexClientId(state: IStoreState) {
      return state.yandexClientId;
    },
    validateUserPlatform(state: IStoreState) {
      if (state.platform === "web") {
        if (window.matchMedia("(display-mode: standalone)").matches) {
          return "Fstore";
        } else {
          return "Web";
        }
      } else if (state.platform === "android") {
        return "Android";
      } else if (state.platform === "ios") {
        return "IOS";
      } else {
        return false;
      }
    },
    getConnectionState(state: IStoreState): boolean {
      return state.connectionState;
    },
    getAppInfo(state: IStoreState): IAppInfo | null {
      return state.appInfo;
    },
    yandexAppEnabled(state: IStoreState): boolean | null {
      return state.yandexAppEnabled;
    },
    getUserData(state: IStoreState): IUserData | null {
      return state.userData;
    },
    getLinkSupport(state: IStoreState): ILinkSupport | null {
      return state.linkSupport;
    },
    getCurrentAppVersion(state: IStoreState): string {
      return state.currentAppVersion;
    },
    getAppVersion(state: IStoreState): IAppVersion | null {
      return state.appVersion;
    },
    getUserCards(state: IStoreState): ICard[] | undefined {
      return state.userCards;
    },
    getDeferredPromo(state: IStoreState): unknown {
      return state.deferredPrompt;
    },
    getCurrentLoginStep(state: IStoreState): ELoginStep {
      return state.currentLoginStep;
    },
    getLoginPhone(state: IStoreState): string {
      return state.loginPhone;
    },
    getLoginPassword(state: IStoreState): string {
      return state.loginPassword;
    },
    getIsNeedRegistration(state: IStoreState): boolean {
      return state.isNeedRegistration;
    },
    getDocuments(state: IStoreState): IDocument[] {
      return state.documents;
    },
  },
  mutations: {
    setBrandStatus(state: IStoreState, data: IBrandStatus): void {
      state.brandStatus = data;
    },
    setCurrentCommission(state: IStoreState, commission: IComission | null) {
      state.currentCommission = commission;
    },
    setIsOpenNewVersionModal(state: IStoreState, isOpen: boolean): void {
      state.isOpenNewVersionModal = isOpen;
    },
    setVersionReminderDismissed(state: IStoreState, dismissed: boolean): void {
      state.versionReminderDismissed = dismissed;
      if (dismissed) {
        state.lastReminderTime = Date.now();
      }
    },
    changeConnectionState(state: IStoreState, value: boolean): void {
      state.connectionState = value;
    },
    setUserData(state: IStoreState, data: IUserData): void {
      state.userData = data;
    },
    setAppInfo(state: IStoreState, data: IAppInfo): void {
      state.appInfo = data;
      state.appName = data.app_name;
      state.yandexClientId = data.yandex_client_id;
      state.isActive = data.is_active;
    },
    setYandexAppEnabled(state: IStoreState, data: boolean): void {
      state.yandexAppEnabled = data;
    },
    setAppVersion(state: IStoreState, data: IAppVersion): void {
      state.appVersion = data;
    },
    setLinkSupport(state: IStoreState, data: ILinkSupport): void {
      state.linkSupport = data;
    },
    setUserCards(state: IStoreState, data: ICard[]): void {
      state.userCards = data;
    },
    setDeferredPrompt(state: IStoreState, data: unknown) {
      state.deferredPrompt = data;
    },
    setCurrentStep(state: IStoreState, data: ELoginStep) {
      state.currentLoginStep = data;
    },
    setLoginPhone(state: IStoreState, value: string) {
      state.loginPhone = value;
    },
    setLoginPassword(state: IStoreState, value: string) {
      state.loginPassword = value;
    },
    setIsNeedRegistration(state: IStoreState, value: boolean) {
      state.isNeedRegistration = value;
    },
    setDocuments(state: IStoreState, documents: IDocument[]): void {
      state.documents = documents;
    },
    setDocumentSigned(
      state: IStoreState,
      payload: { id: string; url: string },
    ): void {
      const document = state.documents.find((d) => d.id === payload.id);
      if (document) {
        document.url = payload.url;
        document.isSigned = true;
      }
    },
    setDocumentNotNew(state: IStoreState, id: string): void {
      const document = state.documents.find((d) => d.id === id);
      if (document) {
        document.isNew = false;
      }
    },
  },
  actions: {
    async getBrandStatus(context) {
      const response = await ApiEnter.getBrandStatus();
      if (UtilError.showBackendErrorBody(response)) return;
      if (typeof response != "boolean")
        context.commit("setBrandStatus", response);
    },
    async fetchCommission({ commit }, commissionId: string) {
      if (!commissionId) {
        commit("setCurrentCommission", null);
        return;
      }
      try {
        const response = await ApiHome.getComission(commissionId);
        if (UtilError.showBackendErrorBody(response)) {
          commit("setCurrentCommission", null);
          return;
        }
        // Бек возвращает один объект комиссии; массив (если вдруг придет)
        // нормализуем к первому элементу, чтобы drawer "условий вывода"
        // всегда получал объект IComission, а не null.
        const commission = Array.isArray(response)
          ? response[0] ?? null
          : response;
        if (commission && typeof commission === "object") {
          commit("setCurrentCommission", commission);
        } else {
          commit("setCurrentCommission", null);
        }
      } catch (error) {
        console.error("Failed to fetch commission:", error);
        commit("setCurrentCommission", null);
      }
    },
    openNewVersionModal({ commit }) {
      commit("setIsOpenNewVersionModal", true);
      commit("setVersionReminderDismissed", false);
    },
    remindLater({ commit }) {
      commit("setVersionReminderDismissed", true);
      commit("setIsOpenNewVersionModal", false);
    },

    async getAppInfo(context) {
      const response = await ApiEnter.getAppInfo();
      if (UtilError.showBackendErrorBody(response)) return;
      if (typeof response != "boolean") context.commit("setAppInfo", response);
    },

    async getYandexAppEnabled(context) {
      const response = await ApiEnter.getYandexAppEnabled();
      if (UtilError.showBackendErrorBody(response)) return;
      if (typeof response === "boolean")
        context.commit("setYandexAppEnabled", response);
    },

    async getUserData(context) {
      const response = await ApiEnter.getAccountUser();
      // Тело ошибки (напр. 500 { detail: ERROR, ... }) в стейт не кладем,
      // чтобы не затирать корректные данные и не ронять шаблоны.
      if (UtilError.showBackendErrorBody(response)) return;
      if (typeof response != "boolean") context.commit("setUserData", response);
    },

    async getAppVersion({ commit }) {
      try {
        const response = await ApiEnter.getAppVersion();
        if (UtilError.showBackendErrorBody(response)) return;
        commit("setAppVersion", response);
        return response;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },

    async getLinkSupport(context) {
      const response = await ApiLinkSupport.getLinkSupport();
      if (UtilError.showBackendErrorBody(response)) return;
      if (typeof response != "boolean")
        context.commit("setLinkSupport", response);
    },

    async fetchUserCards(context) {
      const response = await ApiCard.getCards();
      if (UtilError.showBackendErrorBody(response)) return;
      // Коммитим только массив: тело ошибки и false/null стейт не портят.
      if (Array.isArray(response)) context.commit("setUserCards", response);
    },
    async logout(context) {
      context.commit("setUserData", null);
      context.commit("setUserCards", undefined);
    },
  },
  modules: {},
});
